import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import { categories, comics, faqData } from '@/mocks/comics';

export default function Home() {
  useEffect(() => {
    document.title = '韩漫免费观看在线阅读漫画-韩国漫画免费漫画大全-奇妙漫画网';
  }, []);

  const hotComics = comics.filter(c => c.rating >= 4.7).slice(0, 6);
  const latestComics = [...comics].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()).slice(0, 8);
  const koreanComics = comics.filter(c => c.categories.includes('korean')).slice(0, 4);
  const japaneseComics = comics.filter(c => c.categories.includes('japanese')).slice(0, 4);
  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '奇妙漫画网',
    url: 'https://www.qimiaomanhua.com/',
    description: '奇妙漫画网提供韩漫免费观看在线阅读漫画，韩国漫画免费漫画大全，一站式阅读体验。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.qimiaomanhua.com/category/all?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '奇妙漫画网',
    url: 'https://www.qimiaomanhua.com/',
    description: '奇妙漫画网提供韩漫免费观看在线阅读漫画，韩国漫画免费漫画大全，一站式阅读体验。',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@qimiaomanhua.com',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.slice(0, 5).map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(homeSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Artistic%20vibrant%20manga%20style%20illustration%20with%20warm%20orange%20and%20amber%20gradient%20background%2C%20abstract%20comic%20panels%20and%20speech%20bubbles%20floating%2C%20stylized%20minimal%20design%2C%20soft%20glowing%20atmosphere%2C%20modern%20webtoon%20aesthetic%2C%20clean%20artistic%20composition%2C%20no%20text%2C%20decorative%20pattern&width=1600&height=900&seq=hero-comic-bg&orientation=landscape"
              alt="奇妙漫画网 - 韩漫免费阅读"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-4 leading-tight">
                韩漫免费观看在线阅读漫画
              </h1>
              <p className="text-base md:text-lg text-white/90 max-w-xl mb-6 leading-relaxed">
                韩国漫画免费漫画大全，一站式阅读体验。热门韩漫、日漫每日更新，高清画质，流畅阅读。
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <Link
                  to="/category/all"
                  className="px-8 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
                >
                  开始阅读
                </Link>
                <Link
                  to="/category/korean"
                  className="px-8 py-3 bg-white/20 backdrop-blur-sm text-white border border-white/30 font-medium rounded-full hover:bg-white/30 transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
                >
                  韩漫专区
                </Link>
              </div>
            </div>
          </section>

          {/* Hot Recommendations */}
          <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-14">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  热门推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">万人好评的韩漫精选，不容错过</p>
              </div>
              <Link
                to="/category/all"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-5">
              {hotComics.map(comic => (
                <ComicCard key={comic.id} comic={comic} size="medium" />
              ))}
            </div>
          </section>

          {/* Category Browse */}
          <section className="bg-background-100 py-14">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  分类浏览
                </h2>
                <p className="text-sm text-foreground-500 mt-1">按类型探索你喜欢的漫画世界</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {categories.map(cat => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-3 px-5 py-4 rounded-xl bg-background-50 hover:bg-primary-50 border border-background-200 hover:border-primary-200 transition-all duration-200 group cursor-pointer"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-200">
                      <i className={`${cat.icon} text-lg`}></i>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-foreground-900 whitespace-nowrap">{cat.name}</span>
                      <p className="text-xs text-foreground-400">{cat.count}部</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Korean Comics Zone */}
          <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-14">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  韩漫专区
                </h2>
                <p className="text-sm text-foreground-500 mt-1">最受欢迎的韩国漫画精选</p>
              </div>
              <Link
                to="/category/korean"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                更多韩漫
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {koreanComics.map(comic => (
                <ComicCard key={comic.id} comic={comic} size="large" />
              ))}
            </div>
          </section>

          {/* Japanese Comics Zone */}
          <section className="bg-background-100 py-14">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                    日漫专区
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">经典日本漫画作品汇聚</p>
                </div>
                <Link
                  to="/category/japanese"
                  className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  更多日漫
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                {japaneseComics.map(comic => (
                  <ComicCard key={comic.id} comic={comic} size="large" />
                ))}
              </div>
            </div>
          </section>

          {/* Latest Updates */}
          <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-14">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  最新更新
                </h2>
                <p className="text-sm text-foreground-500 mt-1">最新章节抢先看，追更不等待</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {latestComics.map(comic => (
                <div key={comic.id} className="bg-background-50 rounded-xl border border-background-200 p-4 hover:border-primary-200 transition-all duration-200 group cursor-pointer">
                  <Link to={`/article/${comic.id}`} className="flex gap-4">
                    <div className="w-20 h-28 flex-shrink-0 rounded-lg overflow-hidden">
                      <img
                        src={comic.coverImage}
                        alt={comic.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors mb-1">{comic.title}</h3>
                      <p className="text-xs text-foreground-500 mb-2">{comic.author}</p>
                      <div className="flex items-center gap-1 text-xs text-accent-500 mb-2">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-time-line text-xs"></i>
                        </div>
                        <span>更新至第{comic.chapters.length}话</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-foreground-400">
                        <span>{formatViews(comic.views)}阅读</span>
                        <span>★ {comic.rating}</span>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ Section */}
          <section className="bg-background-100 py-14">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  常见问题
                </h2>
                <p className="text-sm text-foreground-500 mt-1">关于奇妙漫画网，你想知道的都在这里</p>
              </div>
              <div className="max-w-3xl mx-auto space-y-3">
                {faqData.map((faq, idx) => (
                  <details key={idx} className="group bg-background-50 rounded-xl border border-background-200 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer select-none">
                      <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-180 transition-transform duration-200">
                        <i className="ri-arrow-down-s-line"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="relative py-16 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20artistic%20manga%20library%20atmosphere%2C%20soft%20glowing%20bookshelves%20with%20colorful%20comic%20books%2C%20cozy%20reading%20corner%2C%20warm%20amber%20lighting%2C%20stylized%20minimal%20illustration%2C%20inviting%20mood%2C%20clean%20composition&width=1600&height=600&seq=cta-comic-bg&orientation=landscape"
              alt="开始阅读漫画"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
            <div className="relative max-w-[1280px] mx-auto px-4 md:px-6 text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
                开启你的漫画阅读之旅
              </h2>
              <p className="text-sm md:text-base text-white/80 max-w-lg mx-auto mb-6">
                海量韩漫日漫资源，高清画质流畅阅读，每日更新，完全免费。现在就加入奇妙漫画网！
              </p>
              <Link
                to="/category/all"
                className="inline-flex px-8 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
              >
                立即探索
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}